// Gói chú thích VolView cất ở RIS (bảng VolViewChuThich theo StudyInstanceUID). Tệp phiên gốc của VolView nhét cả
// ảnh DICOM đã tải vào (hàng trăm MB) nên RIS chỉ cất phần chú thích: thước đo, hình chữ nhật, đa giác, lớp phân vùng.
// Mã ảnh (imageID) đổi mỗi lần mở, nên mỗi chú thích ghi thêm khối ảnh nó thuộc về (volumes: imageID -> khóa khối);
// mở lại thì ghép theo khóa khối. Khối chưa mở trong phiên này vẫn giữ nguyên chú thích khi lưu lại (omitImages + merge).
// Đặc tả: RisSolution docs/thiet-ke/dac-ta/2026-09-29-luu-chu-thich-volview.md

export const RIS_FORMAT = 'volview-chu-thich-ris';
export const RIS_VERSION = 1;
export const RIS_MANIFEST = 'manifest.json';
export const ANNOTATION_KEYS = ['rulers', 'rectangles', 'polygons'] as const;
export type AnnotationKey = (typeof ANNOTATION_KEYS)[number];

export type SerializedTool = {
  id?: string;
  imageID: string;
  label?: string;
  [prop: string]: unknown;
};
export type SerializedLabel = { labelName?: string; [prop: string]: unknown };
export type SerializedToolGroup = {
  tools: SerializedTool[];
  labels: Record<string, SerializedLabel>;
};
export type SerializedSegmentGroup = {
  id: string;
  path?: string;
  metadata: { name: string; parentImage: string; [prop: string]: unknown };
  [prop: string]: unknown;
};

export type RisManifest = {
  risFormat: typeof RIS_FORMAT;
  risVersion: number;
  volumes: Record<string, string>;
  tools: Partial<Record<AnnotationKey, SerializedToolGroup>>;
  segmentGroups: SerializedSegmentGroup[];
};

export const emptyRisManifest = (): RisManifest => ({
  risFormat: RIS_FORMAT,
  risVersion: RIS_VERSION,
  volumes: {},
  tools: {},
  segmentGroups: [],
});

// Series + số lát + bên chụp: khối có thêm ảnh sau lần lưu thì KHÔNG khớp — chú thích nằm chờ chứ không gắn lệch lát.
export const volumeKeyOf = (info: {
  SeriesInstanceUID: string;
  NumberOfSlices: number;
  Laterality?: string;
}) =>
  `${info.SeriesInstanceUID}|${info.NumberOfSlices}|${(info.Laterality ?? '').trim()}`;

function filterByImages(
  m: RisManifest,
  keep: (imageID: string) => boolean
): RisManifest {
  const tools: RisManifest['tools'] = {};
  ANNOTATION_KEYS.forEach((key) => {
    const nhom = m.tools[key];
    if (!nhom) return;
    tools[key] = {
      tools: nhom.tools.filter((t) => keep(t.imageID)),
      labels: { ...nhom.labels },
    };
  });
  return {
    ...m,
    volumes: Object.fromEntries(
      Object.entries(m.volumes).filter(([id]) => keep(id))
    ),
    tools,
    segmentGroups: m.segmentGroups.filter((g) => keep(g.metadata.parentImage)),
  };
}

export const pickForImages = (m: RisManifest, imageIDs: Set<string>) =>
  filterByImages(m, (id) => imageIDs.has(id));

export const omitImages = (m: RisManifest, imageIDs: Set<string>) =>
  filterByImages(m, (id) => !imageIDs.has(id));

// Gộp phần của khối chưa mở (cho) vào chú thích đang có (hienTai). Mã nhãn là số đếm theo phiên nên hai phiên có thể trùng
// mã mà khác nghĩa: nhãn của phần chờ trùng mã thì đổi mã, công cụ của nó trỏ theo mã mới.
export function mergeRisManifests(
  hienTai: RisManifest,
  cho: RisManifest
): RisManifest {
  const tools: RisManifest['tools'] = {};
  ANNOTATION_KEYS.forEach((key) => {
    const a = hienTai.tools[key];
    const b = cho.tools[key];
    if (!a && !b) return;
    const labels = { ...(a?.labels ?? {}) };
    const doiMa: Record<string, string> = {};
    Object.entries(b?.labels ?? {}).forEach(([ma, nhan]) => {
      let moi = ma;
      for (let i = 1; moi in labels; i++) moi = `${ma}~${i}`;
      doiMa[ma] = moi;
      labels[moi] = nhan;
    });
    tools[key] = {
      tools: [
        ...(a?.tools ?? []),
        ...(b?.tools ?? []).map((t) =>
          t.label ? { ...t, label: doiMa[t.label] ?? t.label } : t
        ),
      ],
      labels,
    };
  });
  return {
    ...hienTai,
    volumes: { ...cho.volumes, ...hienTai.volumes },
    tools,
    segmentGroups: [...hienTai.segmentGroups, ...cho.segmentGroups],
  };
}

export function parseRisManifest(text: string): RisManifest {
  const m = JSON.parse(text);
  if (!m || typeof m !== 'object' || m.risFormat !== RIS_FORMAT)
    throw new Error('Không phải gói chú thích VolView của RIS.');
  return {
    ...emptyRisManifest(),
    ...m,
    volumes: m.volumes ?? {},
    tools: m.tools ?? {},
    segmentGroups: m.segmentGroups ?? [],
  };
}
